import type { AthenaCliUI } from "../cli/ui/index.ts";
import type {
	AthenaPostgresClient,
	AthenaPostgresPool,
	AthenaPostgresQueryable,
} from "../postgres/driver.ts";
import { MigrationError } from "./types.ts";

export interface EmbeddedSqlRelation {
	readonly schema: string;
	readonly table: string;
}

export interface EmbeddedSqlMigration {
	readonly checksum: string;
	readonly filename: string;
	readonly legacyChecksums?: readonly string[];
	readonly name: string;
	readonly requiredRelations?: readonly EmbeddedSqlRelation[];
	readonly sql: string;
	readonly version: number;
}

export type EmbeddedLedgerEntryStatus =
	| "applied"
	| "pending"
	| "legacy-compatible"
	| "checksum-mismatch";

export interface EmbeddedLedgerEntry {
	readonly checksum: string;
	readonly filename: string;
	readonly name: string;
	readonly status: EmbeddedLedgerEntryStatus;
	readonly storedChecksum?: string;
	readonly version: number;
}

export interface EmbeddedLedgerInspection {
	readonly entries: readonly EmbeddedLedgerEntry[];
	readonly ledgerExists: boolean;
	readonly ledgerTable: string;
}

const LEDGER_TABLE_PATTERN =
	/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)?$/;

export function assertLedgerTableName(ledgerTable: string): string {
	if (!LEDGER_TABLE_PATTERN.test(ledgerTable)) {
		throw new MigrationError(
			"CONFIG",
			`Invalid embedded migration ledger table: ${ledgerTable}`,
		);
	}
	return ledgerTable;
}

export function quoteLedgerTable(ledgerTable: string): string {
	return assertLedgerTableName(ledgerTable)
		.split(".")
		.map((part) => `"${part}"`)
		.join(".");
}

function ledgerChecksum(
	row: Record<string, unknown> | undefined,
): string | undefined {
	const value = row?.checksum;
	return typeof value === "string" ? value : undefined;
}

export async function embeddedLedgerExists(
	queryable: AthenaPostgresQueryable,
	ledgerTable: string,
): Promise<boolean> {
	const name = assertLedgerTableName(ledgerTable);
	const result = await queryable.query<{ oid: string | null }>(
		"SELECT to_regclass($1) AS oid",
		[name],
	);
	const oid = result.rows[0]?.oid;
	return oid != null && String(oid).length > 0;
}

async function readLedgerChecksum(
	queryable: AthenaPostgresQueryable,
	ledgerTable: string,
	version: number,
): Promise<string | undefined> {
	const quoted = quoteLedgerTable(ledgerTable);
	const applied = await queryable.query(
		`SELECT checksum FROM ${quoted} WHERE version = $1`,
		[version],
	);
	return ledgerChecksum(applied.rows[0] as Record<string, unknown> | undefined);
}

export function isLegacyLedgerChecksum(
	migration: EmbeddedSqlMigration,
	stored: string,
): boolean {
	return migration.legacyChecksums?.includes(stored) === true;
}

export async function physicalRelationsPresent(
	queryable: AthenaPostgresQueryable,
	relations: readonly EmbeddedSqlRelation[] | undefined,
): Promise<boolean> {
	if (relations == null || relations.length === 0) {
		return false;
	}
	for (const relation of relations) {
		const qualified = `${relation.schema}.${relation.table}`;
		const result = await queryable.query<{ oid: string | null }>(
			"SELECT to_regclass($1) AS oid",
			[qualified],
		);
		const oid = result.rows[0]?.oid;
		if (oid == null || String(oid).length === 0) {
			return false;
		}
	}
	return true;
}

function checksumMismatchMessage(
	migration: EmbeddedSqlMigration,
	stored: string,
	physicalOk: boolean,
): string {
	const legacy = isLegacyLedgerChecksum(migration, stored);
	if (legacy && !physicalOk) {
		return [
			`${migration.filename} version ${migration.version} ledger identity is legacy (${stored}) but the physical schema does not match the packaged generation.`,
			`Packaged checksum: ${migration.checksum}.`,
			"Athena will not rewrite the ledger. Restore the schema or apply a forward migration.",
		].join(" ");
	}
	return [
		`${migration.filename} version ${migration.version} checksum mismatch.`,
		`Ledger: ${stored}.`,
		`Package: ${migration.checksum}.`,
		legacy
			? "Legacy identity is listed but physical fingerprint was not confirmed."
			: "Unknown checksum drift fails closed.",
	].join(" ");
}

async function classifyStoredChecksum(input: {
	migration: EmbeddedSqlMigration;
	queryable: AthenaPostgresQueryable;
	stored: string;
}): Promise<EmbeddedLedgerEntryStatus> {
	const { migration, queryable, stored } = input;
	if (stored === migration.checksum) {
		return "applied";
	}
	if (!isLegacyLedgerChecksum(migration, stored)) {
		return "checksum-mismatch";
	}
	const physicalOk = await physicalRelationsPresent(
		queryable,
		migration.requiredRelations,
	);
	return physicalOk ? "legacy-compatible" : "checksum-mismatch";
}

async function adoptLegacyLedgerChecksum(input: {
	client: AthenaPostgresClient;
	ledgerTable: string;
	migration: EmbeddedSqlMigration;
	stored: string;
}): Promise<void> {
	const quoted = quoteLedgerTable(input.ledgerTable);
	await input.client.query("BEGIN");
	try {
		const updated = await input.client.query(
			`UPDATE ${quoted}
			 SET checksum = $1, name = $2
			 WHERE version = $3 AND checksum = $4`,
			[
				input.migration.checksum,
				input.migration.name,
				input.migration.version,
				input.stored,
			],
		);
		const rowCount =
			typeof (updated as { rowCount?: number }).rowCount === "number"
				? (updated as { rowCount: number }).rowCount
				: 0;
		if (rowCount < 1) {
			throw new MigrationError(
				"INTEGRITY",
				`${input.migration.filename} version ${input.migration.version} legacy adoption updated 0 ledger rows.`,
			);
		}
		await input.client.query("COMMIT");
	} catch (error) {
		try {
			await input.client.query("ROLLBACK");
		} catch {
			// Session may already be aborted.
		}
		throw error;
	}
}

export async function inspectEmbeddedSqlLedger(input: {
	ledgerTable: string;
	migrations: readonly EmbeddedSqlMigration[];
	queryable: AthenaPostgresQueryable;
}): Promise<EmbeddedLedgerInspection> {
	const ledgerExists = await embeddedLedgerExists(
		input.queryable,
		input.ledgerTable,
	);
	const entries: EmbeddedLedgerEntry[] = [];
	for (const migration of input.migrations) {
		if (!ledgerExists) {
			entries.push({
				checksum: migration.checksum,
				filename: migration.filename,
				name: migration.name,
				status: "pending",
				version: migration.version,
			});
			continue;
		}
		const stored = await readLedgerChecksum(
			input.queryable,
			input.ledgerTable,
			migration.version,
		);
		if (stored == null) {
			entries.push({
				checksum: migration.checksum,
				filename: migration.filename,
				name: migration.name,
				status: "pending",
				version: migration.version,
			});
			continue;
		}
		const status = await classifyStoredChecksum({
			migration,
			queryable: input.queryable,
			stored,
		});
		entries.push({
			checksum: migration.checksum,
			filename: migration.filename,
			name: migration.name,
			status,
			storedChecksum: stored,
			version: migration.version,
		});
	}
	return {
		entries,
		ledgerExists,
		ledgerTable: input.ledgerTable,
	};
}

/**
 * Ledger-first embedded SQL apply. Probe `to_regclass` outside BEGIN so a
 * missing ledger is not an undefined-table error that aborts the transaction.
 */
export async function applyEmbeddedSqlMigrations(input: {
	ledgerTable: string;
	migrations: readonly EmbeddedSqlMigration[];
	pool: AthenaPostgresPool;
	ui: AthenaCliUI;
}): Promise<void> {
	const client = await input.pool.connect();
	try {
		for (const migration of input.migrations) {
			const ledgerExists = await embeddedLedgerExists(
				client,
				input.ledgerTable,
			);
			if (ledgerExists) {
				const stored = await readLedgerChecksum(
					client,
					input.ledgerTable,
					migration.version,
				);
				if (stored != null) {
					if (stored === migration.checksum) {
						continue;
					}
					if (isLegacyLedgerChecksum(migration, stored)) {
						const physicalOk = await physicalRelationsPresent(
							client,
							migration.requiredRelations,
						);
						if (!physicalOk) {
							throw new MigrationError(
								"INTEGRITY",
								checksumMismatchMessage(migration, stored, false),
							);
						}
						await adoptLegacyLedgerChecksum({
							client,
							ledgerTable: input.ledgerTable,
							migration,
							stored,
						});
						input.ui.info(
							`✓ ${migration.filename} adopted (legacy ${stored} → packaged checksum)`,
						);
						continue;
					}
					throw new MigrationError(
						"INTEGRITY",
						checksumMismatchMessage(migration, stored, false),
					);
				}
			}
			await applyPendingMigration(client, input.ledgerTable, migration);
			input.ui.info(`✓ ${migration.filename} applied`);
		}
	} finally {
		client.release();
	}
}

async function applyPendingMigration(
	client: AthenaPostgresClient,
	ledgerTable: string,
	migration: EmbeddedSqlMigration,
): Promise<void> {
	const quoted = quoteLedgerTable(ledgerTable);
	await client.query("BEGIN");
	try {
		await client.query(migration.sql);
		await client.query(
			`INSERT INTO ${quoted} (version, name, checksum)
			 VALUES ($1, $2, $3)
			 ON CONFLICT (version) DO NOTHING`,
			[migration.version, migration.name, migration.checksum],
		);
		await client.query("COMMIT");
	} catch (error) {
		try {
			await client.query("ROLLBACK");
		} catch {
			// Session may already be aborted.
		}
		throw error;
	}
}
